'use client'

import { useState, useCallback } from 'react'
import Image from 'next/image'
import { ChevronLeft, ChevronRight, BedDouble } from 'lucide-react'
import { useSwipe } from '../hooks/useSwipe'
import { optimizeStorageImage } from '@/lib/image-utils'

interface RoomImageCarouselProps {
  images: Array<{ url: string; isPrimary: boolean }>
  roomName: string
}

export function RoomImageCarousel({ images, roomName }: RoomImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(i + 1, images.length - 1))
  }, [images.length])

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(i - 1, 0))
  }, [])

  const swipeHandlers = useSwipe({
    onSwipeLeft: goNext,
    onSwipeRight: goPrev,
  })

  if (images.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center min-h-[176px]">
        <BedDouble className="h-12 w-12 text-gray-300 dark:text-gray-600" />
      </div>
    )
  }

  if (images.length === 1) {
    return (
      <Image
        src={optimizeStorageImage(images[0]!.url, { w: 500, f: 'webp', q: 75 })}
        alt={roomName}
        fill
        className="object-cover"
        sizes="(max-width: 640px) 100vw, 224px"
        loading="lazy"
        unoptimized
      />
    )
  }

  return (
    <div className="relative w-full h-full overflow-hidden group" {...swipeHandlers}>
      {/* Images */}
      {/* RTL-aware slide: in dir=rtl the flex row lays out right-to-left, so
          slide i sits at -i*100% already — translating negative would move it
          further off-screen (blank carousel). The rtl: variant flips the sign. */}
      <div
        className="flex h-full transition-transform duration-300 ease-out [transform:translateX(calc(var(--slide)*-100%))] rtl:[transform:translateX(calc(var(--slide)*100%))]"
        style={{ '--slide': currentIndex } as React.CSSProperties}
      >
        {images.map((img, i) => (
          <div key={i} className="relative w-full h-full shrink-0">
            <Image
              src={optimizeStorageImage(img.url, { w: 500, f: 'webp', q: 75 })}
              alt={`${roomName} ${i + 1}`}
              fill
              className="object-cover"
              sizes="(max-width: 640px) 100vw, 224px"
              loading="lazy"
              unoptimized
            />
          </div>
        ))}
      </div>

      {/* Desktop arrows - visible on hover */}
      {currentIndex > 0 && (
        <button
          type="button"
          className="absolute left-1.5 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity min-w-[32px] min-h-[32px] flex items-center justify-center"
          onClick={goPrev}
          aria-label="Previous image"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {currentIndex < images.length - 1 && (
        <button
          type="button"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity min-w-[32px] min-h-[32px] flex items-center justify-center"
          onClick={goNext}
          aria-label="Next image"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {/* Dot indicators */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
        {images.length <= 6 ? (
          images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`rounded-full transition-all ${
                i === currentIndex
                  ? 'w-1.5 h-1.5 bg-white'
                  : 'w-1 h-1 bg-white/50'
              }`}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Image ${i + 1}`}
            />
          ))
        ) : (
          <span className="text-white text-[10px] font-medium leading-none">
            {currentIndex + 1}/{images.length}
          </span>
        )}
      </div>
    </div>
  )
}
